import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { State } from "@App/state";
import { useTranslation } from "react-i18next";
import {
  ServiceIsElectronicLicenseControl,
  ServiceIsVehicleReturn,
} from "@App/utils/conditionals/services";
import { PremiumServiceTypes, Services } from "@App/types/enums";
import { PremiumService } from "@App/types";

type ButtonState = { text: string };

export const useLocationStepDefinition = (onSubmit: () => void) => {
  const { t } = useTranslation();

  const service = useSelector((state: State) => state.orders.service);
  const place = useSelector((state: State) => state.orders.place);
  const premiumServices = useSelector((state: State) => state.orders.premiumServices);

  const [stepTitle, setStepTitle] = useState<string>(t("Waehlen Sie Ihren Wunschort."));
  const [error, setError] = useState<string | null>(null);
  const [button, setButton] = useState<ButtonState>({ text: t("Werkstatt suchen") });

  /**
   * Determine if a tire service is stored (either main service or a premium add-on).
   */
  const someServiceIsTireServiceStored = useCallback((): boolean => {
    if (!service) return false;
    if (service.id === Services.WHEELCHANGETIRESSTORED) return true;

    return (premiumServices ?? []).some(
      (premiumService: PremiumService) =>
        premiumService?.type === PremiumServiceTypes.TYRECHANGEALREADYINSTORAGE,
    );
  }, [service, premiumServices]);

  /**
   * Update titles and button label whenever the service or language changes.
   */
  useEffect(() => {
    if (someServiceIsTireServiceStored()) {
      setStepTitle(t("Wo sind ihre Reifen eingelagert?"));
    } else {
      setStepTitle(t("Waehlen Sie Ihren Wunschort."));
    }

    if (service && ServiceIsVehicleReturn(service)) {
      setButton({ text: t("Rueckgabestation suchen") });
    } else if (service && ServiceIsElectronicLicenseControl(service)) {
      setButton({ text: t("homeHints.searchTestCenter") });
    } else {
      setButton({ text: t("Werkstatt suchen") });
    }
  }, [service, someServiceIsTireServiceStored]);

  const stepGoBackLinkTitle = useMemo(() => (place?.name ? place.name : ""), [place?.name]);

  const validateLocationFilter = useCallback((): boolean => {
    if (!place || !place.lat || !place.lon) {
      setError(t("home.steps.location.errors.required"));
      return false;
    }
    setError(null);
    return true;
  }, [place, t]);

  const handleSubmit = useCallback(() => {
    if (validateLocationFilter()) onSubmit();
  }, [validateLocationFilter, onSubmit]);

  return {
    onSubmit: handleSubmit,
    stepTitle,
    stepGoBackLinkTitle,
    button,
    error,
  };
};
